import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { Image } from "@/libraries/design/components";
import { ColumnLayout } from "@/libraries/design/foundations";
import {
    EnrollmentType,
    useExperiment
} from "@/utils/experiment/use-experiment";
import { wacRefreshQ4Fy26Experiment } from "@/utils/experiment/wacRefreshExperiment";

export interface Logo {
    src: string;
    alt: string;
    width?: number;
}

export interface LogosProps {
    logos: Logo[];
    title?: string;
}

export const Logos = ({ logos, title }: LogosProps) => {
    const checkWacRefresh = useExperiment(wacRefreshQ4Fy26Experiment);
    const isWacRefresh =
        checkWacRefresh.type === EnrollmentType.ENROLLED &&
        checkWacRefresh.value;

    const logosComponent = (
        <ul
            css={[
                logosContainerStyle,
                isWacRefresh && wacRefreshContainerStyles,
                isWacRefresh && title && wacRefreshLogosContainerWithTitleStyles
            ]}
        >
            {logos.map((logo, index) => (
                <li key={index}>
                    <Image
                        src={logo.src}
                        alt={logo.alt}
                        style={{
                            ...logoStyles,
                            ...(logo.width
                                ? {
                                      width: `${logo.width}px`,
                                      height: "auto"
                                  }
                                : {})
                        }}
                    />
                </li>
            ))}
        </ul>
    );

    return isWacRefresh ? (
        title ? (
            <ColumnLayout layout="2-10">
                <h3
                    className="h5"
                    css={css({
                        margin: 0,
                        [media.below.md]: {
                            textAlign: "center"
                        }
                    })}
                >
                    {title}
                </h3>
                {logosComponent}
            </ColumnLayout>
        ) : (
            <ColumnLayout layout="12">{logosComponent}</ColumnLayout>
        )
    ) : (
        <div
            css={css({
                display: "flex",
                flexDirection: "column",
                gap: token("space.600"),
                [media.above.md]: {
                    gap: token("space.800")
                },
                textAlign: "center"
            })}
        >
            {title && <h3 css={css({ margin: 0 })}>{title}</h3>}
            {logosComponent}
        </div>
    );
};

const logosContainerStyle = css({
    display: "flex",
    flexWrap: "wrap",
    margin: 0,
    padding: 0,
    listStyleType: "none",
    justifyContent: "center",
    rowGap: token("space.600"),
    columnGap: token("space.400"),
    [media.above.sm]: {
        columnGap: token("space.800")
    },
    [media.above.md]: {
        rowGap: token("space.800")
    },
    alignItems: "center"
});

const wacRefreshLogosContainerWithTitleStyles = css({
    justifyContent: "start",
    [media.below.md]: {
        justifyContent: "center"
    }
});

const wacRefreshContainerStyles = css({
    rowGap: token("space.400")
});

const logoStyles = {
    height: "32px",
    width: "auto",
    maxWidth: "auto",
    filter: "grayscale(100%) brightness(66%)",
    ["-webkit-filter"]: "grayscale(100%) brightness(66%)"
};
